import React from "react";
import { getIn, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { findWorkspaceDataIndexByUuid } from "app/pages/customers/dsok/utils/normalizeDsokWorkspaces";
import DsokWorkspaceDevicesEditor from "./DsokWorkspaceDevicesEditor";

/**
 * Full workspace editor (features / POS / devices) for one workspace index.
 * Formik paths: `customer.workspaces.data.${wsIndex}.*` — same as legacy customer-detail page.
 */
export default function DsokWorkspaceSingleWorkspaceForm({
  wsIndex,
  workspaceUuid,
  customerIdOverride,
  activeWorkspaceSection,
  setActiveWorkspaceSection,
  activeDeviceUuid,
  setActiveDeviceUuid,
  showDialog,
  hideDialog,
  onDeleteWorkspace,
  showDeleteWorkspaceButton = true,
  /** When true (POS workspace detail), show workspace-level Active switch bound to `isActive`. */
  showWorkspaceIsActiveToggle = false,
  /** When true, expose the "Kompresor - betonáreň" device type (concreteStation customers). */
  allowCompressor = false,
}) {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const resolvedIndex =
    workspaceUuid != null && String(workspaceUuid).trim() !== ""
      ? findWorkspaceDataIndexByUuid(
          values?.customer?.workspaces?.data,
          workspaceUuid,
        )
      : wsIndex;

  const activeWorkspace =
    resolvedIndex >= 0
      ? getIn(values, `customer.workspaces.data.${resolvedIndex}`)
      : null;

  const setWorkspaceField = (path, nextValue) => {
    if (resolvedIndex < 0) return;
    setFieldValue(
      `customer.workspaces.data.${resolvedIndex}.${path}`,
      nextValue,
    );
  };

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {showWorkspaceIsActiveToggle ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          sx={{ flexWrap: "wrap" }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              minWidth: { sm: 140 },
              pt: { sm: 0.5 },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={activeWorkspace?.isActive !== false}
                  onChange={(e) =>
                    setWorkspaceField("isActive", e.target.checked)
                  }
                />
              }
              label={t("pages.pos.workspaces.isActiveLabel", {
                defaultValue: "Active",
              })}
              sx={{ m: 0 }}
            />
          </Box>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 30%" }, minWidth: 0 }}>
            <JumboTextField
              fullWidth
              name={`customer.workspaces.data.${resolvedIndex}.uuid`}
              label={t("dsokCustomerDetail.workspaces.uuid", {
                defaultValue: "UUID",
              })}
              InputProps={{ readOnly: true }}
            />
          </Box>
          <Box sx={{ flex: { xs: "1 1 100%", sm: "2 1 0" }, minWidth: 0 }}>
            <JumboTextField
              fullWidth
              required
              name={`customer.workspaces.data.${resolvedIndex}.name`}
              label={t("dsokCustomerDetail.workspaces.name", {
                defaultValue: "Name",
              })}
            />
          </Box>
        </Stack>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <JumboTextField
                fullWidth
                name={`customer.workspaces.data.${resolvedIndex}.uuid`}
                label={t("dsokCustomerDetail.workspaces.uuid", {
                  defaultValue: "UUID",
                })}
                InputProps={{ readOnly: true }}
              />
            </Box>
            {showDeleteWorkspaceButton ? (
              <Box sx={{ flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onDeleteWorkspace}
                  size="small"
                  sx={{ height: 40 }}
                >
                  {t("dsokCustomerDetail.workspaces.delete", {
                    defaultValue: "Delete",
                  })}
                </Button>
              </Box>
            ) : null}
          </Stack>

          <JumboTextField
            fullWidth
            required
            name={`customer.workspaces.data.${resolvedIndex}.name`}
            label={t("dsokCustomerDetail.workspaces.name", {
              defaultValue: "Name",
            })}
          />
        </>
      )}

      <TabContext value={activeWorkspaceSection}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            mb: 1,
          }}
        >
          <TabList
            onChange={(_, v) => setActiveWorkspaceSection(v)}
            aria-label="workspace sections"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
              },
            }}
          >
            <Tab
              value="features"
              label={t("dsokCustomerDetail.workspaces.featuresTab", {
                defaultValue: "Features",
              })}
            />
            <Tab
              value="pos"
              label={t("dsokCustomerDetail.workspaces.posTab", {
                defaultValue: "POS",
              })}
            />
            <Tab
              value="devices"
              label={t("dsokCustomerDetail.workspaces.devicesTab", {
                defaultValue: "Devices",
              })}
            />
          </TabList>
        </Box>

        <TabPanel value="features" sx={{ p: 0 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("dsokCustomerDetail.workspaces.featuresSectionTitle", {
                defaultValue: "Funkcie pracoviska",
              })}
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={Boolean(
                    activeWorkspace?.features?.ticketPrintingEnabled,
                  )}
                  onChange={(e) =>
                    setWorkspaceField(
                      "features.ticketPrintingEnabled",
                      e.target.checked,
                    )
                  }
                />
                <Typography
                  variant="body2"
                  sx={{ ml: 1, whiteSpace: "nowrap" }}
                >
                  {t(
                    "dsokCustomerDetail.workspaces.feature.ticketPrintingEnabled",
                    {
                      defaultValue: "Tlač lístkov povolená",
                    },
                  )}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={Boolean(
                    activeWorkspace?.features?.wristbandPrintingEnabled,
                  )}
                  onChange={(e) =>
                    setWorkspaceField(
                      "features.wristbandPrintingEnabled",
                      e.target.checked,
                    )
                  }
                />
                <Typography
                  variant="body2"
                  sx={{ ml: 1, whiteSpace: "nowrap" }}
                >
                  {t(
                    "dsokCustomerDetail.workspaces.feature.wristbandPrintingEnabled",
                    {
                      defaultValue: "Tlač náramkov povolená",
                    },
                  )}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </TabPanel>

        <TabPanel value="pos" sx={{ p: 0 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t("dsokCustomerDetail.workspaces.posSectionTitle", {
                defaultValue: "POS nastavenia",
              })}
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(activeWorkspace?.pos?.isPosEnabled)}
                    onChange={(e) =>
                      setWorkspaceField("pos.isPosEnabled", e.target.checked)
                    }
                  />
                }
                label={t("dsokCustomerDetail.workspaces.posEnabledLabel", {
                  defaultValue: "POS je povolený",
                })}
              />

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
                {t("dsokCustomerDetail.workspaces.posMenuTitle", {
                  defaultValue: "Menu POS",
                })}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(activeWorkspace?.pos?.menu?.pos?.isActive)}
                    onChange={(e) =>
                      setWorkspaceField(
                        "pos.menu.pos.isActive",
                        e.target.checked,
                      )
                    }
                  />
                }
                label={t("dsokCustomerDetail.workspaces.posMenuPos", {
                  defaultValue: "POS / Aktívny / Zobraziť kategórie",
                })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      activeWorkspace?.pos?.menu?.pos?.showCategories,
                    )}
                    onChange={(e) =>
                      setWorkspaceField(
                        "pos.menu.pos.showCategories",
                        e.target.checked,
                      )
                    }
                  />
                }
                label={t("dsokCustomerDetail.workspaces.posMenuShowCategories", {
                  defaultValue: "Zobraziť kategórie",
                })}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      activeWorkspace?.pos?.menu?.ticketControl?.isActive,
                    )}
                    onChange={(e) =>
                      setWorkspaceField(
                        "pos.menu.ticketControl.isActive",
                        e.target.checked,
                      )
                    }
                  />
                }
                label={t(
                  "dsokCustomerDetail.workspaces.posMenuTicketControl",
                  {
                    defaultValue: "Kontrola lístkov",
                  },
                )}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(
                      activeWorkspace?.pos?.menu?.turnstiles?.isActive,
                    )}
                    onChange={(e) =>
                      setWorkspaceField(
                        "pos.menu.turnstiles.isActive",
                        e.target.checked,
                      )
                    }
                  />
                }
                label={t("dsokCustomerDetail.workspaces.posMenuTurnstiles", {
                  defaultValue: "Správa turniketov",
                })}
              />
            </Stack>
          </Paper>
        </TabPanel>

        <TabPanel value="devices" sx={{ p: 0 }}>
          <DsokWorkspaceDevicesEditor
            workspaceUuid={workspaceUuid}
            customerIdOverride={customerIdOverride}
            activeDeviceUuid={activeDeviceUuid}
            setActiveDeviceUuid={setActiveDeviceUuid}
            showDialog={showDialog}
            hideDialog={hideDialog}
            allowCompressor={allowCompressor}
            t={t}
          />
        </TabPanel>
      </TabContext>
    </Stack>
  );
}
